// Holding a drag to one axis: which axis the pointer is moving along on
// screen, and where along it the pointer puts the product.
import * as THREE from 'three';
import {
    AXES,
    Axis,
    axisIndex,
    axisVector,
} from 'components/customer/RoomPlanner/helpers/axes';

// How far the pointer must move on screen, in pixels, before an axis is
// picked, so a hand resting on Ctrl doesn't pick one at random.
export const AXIS_PICK_THRESHOLD_PX = 6;

// The length of world axis projected to find its on-screen direction and
// scale.
const AXIS_PROBE = 10;

// An axis whose probe covers less than this on screen points almost at the
// camera, so its on-screen direction is noise.
const MIN_PROBE_PX = 1;

// How far, in pixels, the pointer must stray from the held axis's line on
// screen before another axis may take over, so passing back over where Ctrl
// went down, where the pointer's direction means nothing, keeps the axis.
export const AXIS_KEEP_PX = 24;

// How far off a held axis a snap may move a product and still count as on
// it, in scene units.
const ON_AXIS_TOLERANCE = 0.1;

/**
 * Where a hold started: the product's position, the pointer and its ray when
 * Ctrl went down.
 */
export interface AxisAnchor {
    position: THREE.Vector3;
    pointer: THREE.Vector2;
    ray: THREE.Ray;
}

/**
 * The direction a world axis through a point runs on screen, in pixels with y
 * down as pointer events report it.
 * @param {THREE.Vector3} origin The point the axis runs through.
 * @param {Axis} axis The axis.
 * @param {THREE.Camera} camera The camera.
 * @param {THREE.Vector2} viewport The canvas size in pixels.
 * @return {THREE.Vector2} The axis's on-screen extent for one probe length.
 */
export const screenDirection = (
    origin: THREE.Vector3,
    axis: Axis,
    camera: THREE.Camera,
    viewport: THREE.Vector2
): THREE.Vector2 => {
    const from = origin.clone().project(camera);
    const to = origin
        .clone()
        .addScaledVector(axisVector(axis), AXIS_PROBE)
        .project(camera);

    return new THREE.Vector2(
        ((to.x - from.x) * viewport.x) / 2,
        (-(to.y - from.y) * viewport.y) / 2
    );
};

/**
 * @param {THREE.Vector3} a A point.
 * @param {THREE.Vector3} b Another.
 * @param {THREE.Camera} camera The camera.
 * @param {THREE.Vector2} viewport The canvas size in pixels.
 * @return {number} How far apart they are on screen, in pixels.
 */
export const screenDistance = (
    a: THREE.Vector3,
    b: THREE.Vector3,
    camera: THREE.Camera,
    viewport: THREE.Vector2
): number => {
    const from = a.clone().project(camera);
    const to = b.clone().project(camera);

    return Math.hypot(
        ((to.x - from.x) * viewport.x) / 2,
        ((to.y - from.y) * viewport.y) / 2
    );
};

/**
 * @param {THREE.Vector3} origin A point.
 * @param {THREE.Camera} camera The camera.
 * @param {THREE.Vector2} viewport The canvas size in pixels.
 * @return {THREE.Vector3} How many pixels one scene unit along each axis
 * through the point covers on screen, per component.
 */
export const pixelsPerUnit = (
    origin: THREE.Vector3,
    camera: THREE.Camera,
    viewport: THREE.Vector2
): THREE.Vector3 =>
    new THREE.Vector3(
        ...AXES.map(
            (axis) =>
                screenDirection(origin, axis, camera, viewport).length() /
                AXIS_PROBE
        )
    );

// Two axes running within this angle of each other on screen, either way
// along the line, can't be told apart by the pointer: looking low across the
// room, x and z do.
const SAME_LINE = Math.cos((15 * Math.PI) / 180);

/**
 * The axis whose on-screen direction the pointer's movement is closest to,
 * either way along it. Of two axes on nearly the same line on screen, only
 * the longer counts: the shorter is the more foreshortened, so holding it
 * would move the product further than the pointer appears to, in a direction
 * it doesn't appear to go.
 * @param {THREE.Vector2} moved The pointer's on-screen movement since Ctrl
 * went down, in pixels.
 * @param {function(Axis): THREE.Vector2} directionOf Each axis's
 * on-screen direction.
 * @return {Axis | null} The axis, or null before the pointer has moved
 * far enough.
 */
export const pickScreenAxis = (
    moved: THREE.Vector2,
    directionOf: (axis: Axis) => THREE.Vector2
): Axis | null => {
    if (moved.length() < AXIS_PICK_THRESHOLD_PX) return null;

    const directions = AXES.map((axis) => ({
        axis,
        direction: directionOf(axis),
    })).filter(({direction}) => direction.length() >= MIN_PROBE_PX);
    const candidates = directions.filter(
        ({direction}) =>
            !directions.some(
                (other) =>
                    other.direction.length() > direction.length() &&
                    Math.abs(
                        other.direction
                            .clone()
                            .normalize()
                            .dot(direction.clone().normalize())
                    ) > SAME_LINE
            )
    );

    const pointer = moved.clone().normalize();
    // Cast: TypeScript does not follow the assignment in the callback.
    let picked = null as Axis | null;
    let bestAlignment = -1;

    candidates.forEach(({axis, direction}) => {
        const alignment = Math.abs(pointer.dot(direction.clone().normalize()));
        if (alignment > bestAlignment) {
            picked = axis;
            bestAlignment = alignment;
        }
    });

    return picked;
};

/**
 * How far along an axis line the point nearest a ray lies.
 * @param {THREE.Ray} ray The pointer's ray.
 * @param {THREE.Vector3} origin The point the axis line runs through.
 * @param {Axis} axis The axis.
 * @return {number | null} The distance from the origin along the axis, or
 * null when the ray runs along the axis.
 */
export const distanceAlongAxis = (
    ray: THREE.Ray,
    origin: THREE.Vector3,
    axis: Axis
): number | null => {
    const direction = axisVector(axis);
    const toOrigin = origin.clone().sub(ray.origin);
    const cosine = direction.dot(ray.direction);
    const denominator = 1 - cosine * cosine;
    if (denominator < 1e-6) return null;

    return (
        (cosine * ray.direction.dot(toOrigin) - direction.dot(toOrigin)) /
        denominator
    );
};

/**
 * @param {THREE.Vector2} moved The pointer's movement since Ctrl went down.
 * @param {THREE.Vector2} direction An axis's on-screen direction.
 * @return {number} How far the pointer is from that axis's line through
 * where Ctrl went down, in pixels.
 */
const offLine = (moved: THREE.Vector2, direction: THREE.Vector2): number => {
    const along = direction.clone().normalize();

    return Math.abs(moved.x * along.y - moved.y * along.x);
};

/**
 * Where the pointer puts a product held to one axis from its anchor.
 * @param {AxisAnchor} anchor Where the hold started.
 * @param {THREE.Vector2} pointer The pointer now, in pixels.
 * @param {THREE.Ray} ray The pointer's ray now.
 * @param {function(Axis): THREE.Vector2} directionOf Each axis's
 * on-screen direction; a zero direction rules an axis out.
 * @param {Axis | null} [held] The axis the hold is on. It stays until
 * the pointer strays AXIS_KEEP_PX off its line.
 * @return {object | null} The axis and position, or null until the pointer
 * has picked an axis.
 */
export const holdToAxis = (
    anchor: AxisAnchor,
    pointer: THREE.Vector2,
    ray: THREE.Ray,
    directionOf: (axis: Axis) => THREE.Vector2,
    held: Axis | null = null
): {axis: Axis; position: THREE.Vector3} | null => {
    const moved = pointer.clone().sub(anchor.pointer);
    const heldDirection = held === null ? null : directionOf(held);
    const keep =
        heldDirection !== null &&
        heldDirection.length() >= MIN_PROBE_PX &&
        offLine(moved, heldDirection) <= AXIS_KEEP_PX;
    const axis = keep ? held : pickScreenAxis(moved, directionOf);
    if (axis === null) return null;

    const now = distanceAlongAxis(ray, anchor.position, axis);
    const then = distanceAlongAxis(anchor.ray, anchor.position, axis);
    if (now === null || then === null) return null;

    const position = anchor.position.clone();
    const index = axisIndex(axis);
    position.setComponent(index, position.getComponent(index) + now - then);

    return {axis, position};
};

/**
 * @param {THREE.Vector3} offset How far a snap would move a held product.
 * @param {Axis} axis The axis it is held to.
 * @return {boolean} True when the snap moves it along that axis only.
 */
export const onAxis = (offset: THREE.Vector3, axis: Axis): boolean =>
    AXES.every(
        (other) =>
            other === axis ||
            Math.abs(offset.getComponent(axisIndex(other))) <= ON_AXIS_TOLERANCE
    );
